import Splide, { SlideComponent } from '@splidejs/splide';
import { isMobile } from '@clientShared/utils';
import { elementsOf } from '@shared/easyfy.core';

export default class SliderHelper {
  static CreateSlider = (list: HTMLElement): { slider: Splide; excludePagination: boolean } => {
    if (!list) {
      console.error('CreateSlider: list element is null or undefined.');
      return null;
    }

    const articleCount = Number.parseInt(list.getAttribute('data-take') || '0', 10) || 0;

    // Get the container's width
    const containerWidth = list.clientWidth;

    // Calculate the total width required for the cards
    const cardWidth = 270; // Fixed width of each card
    const defaultGap = 32; // Default gap between cards
    const totalCardsWidth = articleCount > 0 ? articleCount * cardWidth + (articleCount - 1) * defaultGap : 0;

    // Determine the slider type and whether to exclude pagination
    const sliderType = totalCardsWidth < containerWidth ? 'slide' : 'loop';
    const excludePagination = totalCardsWidth < containerWidth;

    // Create the slider instance (unmounted)
    const slider = new Splide(list, {
      type: sliderType,
      perMove: 1,
      speed: 300,
      dragMinThreshold: 20,
      flickMaxPages: 1,
      flickPower: 100,
      snap: false,
      arrows: sliderType !== 'slide',
      gap: defaultGap,
      fixedWidth: cardWidth,
      padding: {
        left: 0,
        right: 0,
      },
      breakpoints: {
        1100: {
          gap: 24,
          arrows: false,
        },
        768: {
          gap: 12,
          arrows: false,
        },
      },
    });

    // Return the slider along with the excludePagination flag
    return { slider, excludePagination };
  };

  static OnPaginationMounted = (slider: Splide, list: HTMLElement, excludePagination: boolean = false) => {
    if (!excludePagination) {
      slider.on('pagination:mounted', (data) => {
        // if fewer slides than provided in options, hide pagination
        if (data.items.length <= slider.options.perPage) {
          slider.options.type = 'slide';
          slider.options.pagination = false;
          slider.options.drag = false;
          list.classList.add('hide-arrows');
        }
        data.list.classList.add('with-lines');
      });
    }
  };

  // Splide.js creates cloned slides to make the carousel infinite.
  // To avoid link duplication, we have to replace <a> tags with <span> on invisible copies
  static removeDuplicatedLinks = (slider: Splide) => {
    const slides: SlideComponent[] = slider.Components.Slides.get();
    this.removeLinksOnClonedSlides(slides);

    slider.on('moved', () => {
      this.updateLinks(slider, slides);
    });
  };

  private static updateLinks = (slider: Splide, slides: SlideComponent[]) => {
    const slideMap = this.getSlideVisibilityInfo(slider, slides);

    slideMap.forEach((slide) => {
      const currentEl = slide?.slide?.slide;
      if (!currentEl) return;

      const matchingSlide = [...slideMap.values()].find(
        (s) => s?.slide?.slide?.getAttribute('aria-label') === currentEl.getAttribute('aria-label') && s?.slide?.slide?.id !== currentEl.id
      );

      if (slide.isVisible && !slide.hasLinks) {
        this.addAnchorElement(slide.slide);

        if (matchingSlide) {
          this.removeAnchorElement(matchingSlide.slide);
        }
      } else if (!slide.isVisible && !slide.hasLinks && !matchingSlide) {
        this.addAnchorElement(slide.slide);
      }
    });
  };

  private static getSlideVisibilityInfo = (slider: Splide, slides: SlideComponent[]): Map<string, SlideViewInfo> => {
    const splideTrack = slider.root.querySelector('.splide__track') as HTMLElement;

    const trackRect = splideTrack.getBoundingClientRect();

    let slideMap = new Map<string, SlideViewInfo>();

    slides.forEach((slide) => {
      const slideRect = slide.slide.getBoundingClientRect();
      const isVisible = slideRect.right >= trackRect.left && slideRect.left <= trackRect.right;
      const hasLinks = !!slide.slide.querySelector('a');

      slideMap.set(slide.slide.id, { slide, isVisible, hasLinks });
    });

    return slideMap;
  };

  private static removeLinksOnClonedSlides = (slides: SlideComponent[]) => {
    const clonedSlides = slides.filter((s) => s.isClone);

    clonedSlides.forEach((slide) => {
      this.removeAnchorElement(slide);
    });
  };

  private static removeAnchorElement = (slide: SlideComponent) => {
    const links: NodeListOf<HTMLAnchorElement> = slide.slide.querySelectorAll('a');
    links.forEach((link) => {
      let span = document.createElement('span');

      link.getAttributeNames().forEach((name) => {
        span.setAttribute(name, link.getAttribute(name));
      });

      span.dataset.href = link.getAttribute('href');
      span.replaceChildren(...Array.from(link.childNodes));
      span.removeAttribute('href');

      link?.parentNode?.replaceChild(span, link);
    });
  };

  private static addAnchorElement = (slide: SlideComponent) => {
    const spans: NodeListOf<HTMLSpanElement> = slide.slide.querySelectorAll('[data-href]');
    spans.forEach((span) => {
      const newLink = document.createElement('a');

      span.getAttributeNames().forEach((name) => {
        newLink.setAttribute(name, span.getAttribute(name));
      });

      newLink.href = span.dataset.href;
      newLink.removeAttribute('data-href');
      newLink.replaceChildren(...Array.from(span.childNodes));

      span?.parentNode?.replaceChild(newLink, span);
    });
  };

  static AddSliderOverlay = (list: HTMLElement) => {
    const sliderOverlayEl = document.createElement('div');
    sliderOverlayEl.classList.add('slider-overlay');
    list.appendChild(sliderOverlayEl);
  };

  static UpdateImagesSize = (list: HTMLElement) => {
    if (isMobile) {
      const aEl = elementsOf(list, 'a:not([tabindex="-1"])');
      if (aEl) {
        const img = elementsOf(aEl, 'img.prod-card-img');
        if (img) {
          if (img.complete) {
            this.setImagesSize(list, img);
          } else {
            img.onload = () => {
              this.setImagesSize(list, img);
            };
          }
        }
      }
    }
  };

  private static setImagesSize = (list: HTMLElement, img: HTMLImageElement) => {
    const imgWidth = img.width;
    const imgHeight = img.height;

    list.querySelectorAll('img.prod-card-img').forEach((imgEl) => {
      imgEl.setAttribute('width', imgWidth.toString());
      imgEl.setAttribute('height', imgHeight.toString());
      imgEl.classList.add('horizontal-scroll');
    });
  };
}

export interface SlideViewInfo {
  slide: SlideComponent;
  isVisible: boolean;
  hasLinks: boolean;
}
